// Dialog lifecycle, compact account identity, and persistent selection footer.
import { useEffect, useRef, useState } from 'react';
import type { BookAccount, BookPayload, EnrichCandidate } from '../api/types';
import type { BoardPref } from '../book/boardPref';
import { autoLaneTitle } from '../book/boardPref';
import { recordSignalToFirstTouch } from '../api/client';
import { useAuthContext } from '../auth/AuthContext';
import {
  SALESFORCE_ACCOUNT_URL, companyUrl, formatCount, httpsUrl,
} from '../lib/format';
import type { WorkReason } from '../book/derived';
import { hasSharedSlack, pastOpportunityChip, pastOpportunityDetail, pastOpportunityIsLoss, pastOpportunityOf } from '../book/accountFlags';
import { lastOutbound, recentLostDeal } from '../book/derived';
import { engineFacts } from './clayEngine';
import { CollapseProvider } from './Collapsible';
import { AccountPanel } from './AccountPanel';
import { Separated } from './blocks';
import { accountLink, factStrip, hideAction } from './panelFacts';
import { Q4Warnings } from './Q4Warnings';
import { PriorityFlagButton } from '../book/PriorityFlagButton';
import { usePriorityToggle } from '../book/usePriorityToggle';
import { ACCOUNT_LINK_BASE, panelForPage } from '../lib/roleViews.ts';
import type { AccountsPage, FactStripKind } from '../lib/roleViews.ts';
import { oversightFactStrip } from '../oversight/oversightFacts';
import { aeFactStrip } from '../ae/aeFacts';
import type { StripFact } from './panelFacts';

// Header links. Each is built from a stored field, so a chip is missing exactly when the field
// behind it is null -- there is no guessed URL anywhere in here. The company comes first and
// Salesforce last: the account name in the table links to the company website, so the panel's
// chip order matches what the row already taught.
function SideLinks({ row }: { row: BookAccount }) {
  const domain = companyUrl(row.domain, row.website);
  // Salesforce's stored page first, then the one the OB Engine found. The engine has written a
  // LinkedIn on every account it has assessed and most of those carry no
  // `LinkedIn_Company_Page__c` at all -- so without this fallback the only LinkedIn we hold sits
  // inside the Clay block while the header chip is simply missing.
  const linkedin = row.linkedin_company_page || engineFacts(row).linkedin;

  return (
    <div className="chips">
      {domain ? (
        <a className="linkchip" href={domain} target="_blank" rel="noopener">
          <span className="ic">↗</span>{row.domain || domain}
        </a>
      ) : null}
      {/* A shared Slack Connect channel. Every account carrying one also has an opportunity, so
          this is a company we already talk to daily -- which is why it is styled as a
          relationship rather than as one more outbound link. */}
      {hasSharedSlack(row) ? (
        row.pylon_slack_url ? (
          <a
            className="linkchip slack"
            href={httpsUrl(row.pylon_slack_url) ?? undefined}
            target="_blank"
            rel="noopener"
          >
            <span className="ic">#</span>Shared Slack channel
          </a>
        ) : (
          <span className="linkchip slack">
            <span className="ic">#</span>Shared Slack channel
          </span>
        )
      ) : null}
      {linkedin ? (
        <a
          className="linkchip"
          href={httpsUrl(linkedin) ?? undefined}
          target="_blank"
          rel="noopener"
        >
          <span className="ic">in</span>LinkedIn
        </a>
      ) : null}
      {/* Always present: every row in the book is a Salesforce account, keyed by its id. */}
      <a
        className="linkchip"
        href={`${SALESFORCE_ACCOUNT_URL}${row.id}/view`}
        target="_blank"
        rel="noopener"
      >
        <span className="ic">↗</span>Salesforce
      </a>
    </div>
  );
}

// The role's strip (`lib/roleViews.ts` factStrip).
function stripFacts(kind: FactStripKind, row: BookAccount, today: string): StripFact[] {
  if (kind === 'ae') return aeFactStrip(row, today);
  if (kind === 'oversight') return oversightFactStrip(row, today);
  return factStrip(row, today);
}

// The facts above every tab. The strategist's: stage, plan, usage, AE owner, last touch, the Q4
// priority first. An AE's (`aeFactStrip`): the lead deal, next meeting, SDR owner, usage. The
// oversight page's (`oversightFactStrip`): stage, pipeline, SDR, AE, last activity, usage.
function FactStrip({ facts }: { facts: StripFact[] }) {
  return (
    <dl className="factstrip">
      {facts.map(fact => (
        <div key={fact.label} className={'stripfact' + (fact.title ? ' hint' : '')} title={fact.title ?? undefined}>
          <dt>{fact.label}</dt>
          <dd className={fact.valueTone ?? undefined}>
            {fact.value}
            {fact.note ? <span className={'stripnote' + (fact.tone ? ' ' + fact.tone : '')}> {fact.note}</span> : null}
          </dd>
        </div>
      ))}
    </dl>
  );
}

// The header's ⋯ menu: hide from (or back onto) my board, and the account's link. Salesforce
// is a chip under the name, one click away, not in here.
function HeaderMenu({ row, preference, onSetPref, pending, linkBase }: {
  row: BookAccount;
  preference: BoardPref;
  onSetPref?: (accountId: string, preference: BoardPref) => void;
  pending: boolean;
  linkBase: string;
}) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const hide = hideAction(preference, pending);

  // A click elsewhere or Escape closes it.
  useEffect(() => {
    if (!open) return;
    function onDocumentClick(event: MouseEvent) {
      if (!wrapRef.current?.contains(event.target as Node)) setOpen(false);
    }
    function onDocumentKey(event: KeyboardEvent) {
      if (event.key === 'Escape') { event.stopPropagation(); setOpen(false); }
    }
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKey, true);
    return () => {
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onDocumentKey, true);
    };
  }, [open]);

  async function copyLink() {
    try {
      await navigator.clipboard.writeText(accountLink(window.location.origin, row.id, linkBase));
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  }

  return (
    <div className="headmenu" ref={wrapRef}>
      <button type="button" className="sidemore" aria-label="More actions" aria-haspopup="menu" aria-expanded={open}
        onClick={() => setOpen(current => !current)}>⋯</button>
      {open ? (
        <div className="headmenulist" role="menu">
          {onSetPref ? (
            <button type="button" role="menuitem" disabled={hide.disabled}
              onClick={() => { onSetPref(row.id, hide.next); setOpen(false); }}>
              {hide.label}
            </button>
          ) : null}
          <button type="button" role="menuitem" onClick={() => void copyLink()}>{copied ? 'Copied' : 'Copy account link'}</button>
        </div>
      ) : null}
    </div>
  );
}

export function Sidebar({
  account,
  book,
  onClose,
  onContactEnriched,
  workReason,
  onSetPref,
  prefPending = false,
  page = 'board',
}: {
  account: BookAccount | null;
  book: BookPayload | null;
  onClose: () => void;
  onContactEnriched: (contactId: string, candidate: EnrichCandidate) => void;
  onBookChanged?: () => void;
  workReason?: WorkReason | null;
  // Pin / hide on this person's board. Absent on a surface with no board preference, and under
  // a read-only view-as.
  onSetPref?: (accountId: string, preference: BoardPref) => void;
  // A pin / hide write for this account is in flight.
  prefPending?: boolean;
  // The page showing the panel, which picks its Company info, strip, Scout prompts and rail
  // (`panelForPage`): the board's, the AE book's or the oversight page's.
  page?: AccountsPage;
}) {
  const isOpen = Boolean(account && book);
  const closeRef = useRef<HTMLButtonElement>(null);
  // The account hidden from this panel, so the note can offer Undo until the panel moves on.
  const [hiddenHere, setHiddenHere] = useState<{ accountId: string; previous: BoardPref } | null>(null);
  const { getToken } = useAuthContext();
  // The High priority flag in the header: the same toggle as the board's column.
  const priority = usePriorityToggle(getToken);
  // What the Company info tab shows and which tabs the rail carries, by the page showing the panel
  // (`lib/roleViews.ts`). Everything else in the panel is the same everywhere.
  const view = panelForPage(page);
  // Copied links open on the board, for every role.
  const linkBase = ACCOUNT_LINK_BASE;
  const companyInfo = view.companyInfo;

  // Escape closes the panel. The filter panel has its own Escape handler and cannot be open at
  // the same time -- this marks the whole background `inert` -- so the two never compete.
  useEffect(() => {
    if (!isOpen) return;
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    function keepFocusInside(event: KeyboardEvent) {
      if (event.key !== 'Tab') return;
      const dialog = closeRef.current?.closest('aside');
      if (!dialog) return;
      const focusable = [...dialog.querySelectorAll<HTMLElement>('button,a[href],input,select,textarea,summary,[tabindex="0"]')]
        .filter(element => !element.hasAttribute('disabled') && element.tabIndex >= 0 && element.getClientRects().length > 0);
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
    document.addEventListener('keydown', keepFocusInside);
    return () => { document.removeEventListener('keydown', keepFocusInside); queueMicrotask(() => { if (opener?.isConnected) opener.focus(); }); };
  }, [isOpen]);

  // Keep the book stationary while the independent panel body scrolls.
  useEffect(() => {
    if (!isOpen) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previousOverflow; };
  }, [isOpen]);

  // Everything outside the sidebar leaves the focus order and the accessibility tree while it
  // is open, which is what `aria-modal="true"` has been claiming all along.
  useEffect(() => {
    const background = document.getElementById('app-background');
    if (!background) return;
    if (isOpen) background.setAttribute('inert', '');
    else background.removeAttribute('inert');
    return () => background.removeAttribute('inert');
  }, [isOpen]);

  // Focus the close button whenever the panel moves to a new account. The body remounts per
  // account, so its scroll starts at the top on its own.
  useEffect(() => {
    if (!isOpen) return;
    closeRef.current?.focus();
  }, [isOpen, account?.id]);

  // Week-one number: signal landed → first touch, recorded once per account.
  useEffect(() => {
    // A strategist's measure, and a write: an AE's read-only panel records nothing.
    if (!account || companyInfo === 'ae') return;
    if (!account.first_touch || !account.clay?.signal) return;
    void recordSignalToFirstTouch(getToken, account.id);
  }, [account, getToken, companyInfo]);

  const engine = account ? engineFacts(account) : null;
  const employeeCount = account?.employee_count ?? engine?.employees ?? null;
  const country = account?.country || engine?.country;
  const industry = account?.industry || engine?.industry;
  // Who we last wrote to here. Promoted into the header because it is asked before the panel
  // is scrolled -- a strategist opening an account wants to know they are not about to send a
  // cold email to somebody a colleague emailed on Tuesday.
  const lastOut = account ? lastOutbound(account) : null;
  const lost = account ? recentLostDeal(account, new Date((book?.today || '1970-01-01') + 'T00:00:00Z')) : null;
  const past = account ? pastOpportunityOf(account) : null;
  const preference: BoardPref = account ? book?.board_prefs?.[account.id] ?? null : null;
  const showHiddenNote = Boolean(account && preference === 'hidden' && hiddenHere?.accountId === account.id);

  // Hide remembers what it replaced, so Undo puts back a pin rather than clearing it.
  function setPreference(accountId: string, next: BoardPref) {
    if (!onSetPref || prefPending) return;
    setHiddenHere(next === 'hidden' ? { accountId, previous: preference } : null);
    onSetPref(accountId, next);
  }

  return (
    <>
      <div className={'scrim' + (isOpen ? ' on' : '')} onClick={onClose} />
      <aside
        className={'side' + (isOpen ? ' on' : '')}
        role="dialog"
        aria-modal="true"
        aria-labelledby="sidename"
        aria-hidden={!isOpen}
      >
        {account && book ? (
          // Keyed on the account so every piece of panel-local state -- expanded lists, the
          // push selection, an in-flight enrichment -- is dropped when the panel moves on. A
          // block left expanded would otherwise reopen against a list belonging to another
          // company, and a picker would reopen aimed at contacts that just went away.
          <CollapseProvider key={account.id}>
            <div className="sidehead">
              <div className="sideheadactions">
                <HeaderMenu row={account} preference={preference} onSetPref={onSetPref ? setPreference : undefined} pending={prefPending} linkBase={linkBase} />
                <button className="sideclose" ref={closeRef} aria-label="Close" onClick={onClose}>
                  ×
                </button>
              </div>
              <h2 id="sidename">
                {account.account}
                {hasSharedSlack(account) ? (
                  <span
                    className="slackmark"
                    title="We share a Slack Connect channel with this company"
                  >
                    shared Slack
                  </span>
                ) : null}
                {account.auto_lane ? (
                  <span className="slackmark automark" title={autoLaneTitle(account) ?? undefined}>
                    Auto
                  </span>
                ) : null}
              </h2>

              <p className="sidesub">{account.sdr_owner || 'Unassigned'} · strategist / {account.account_owner || 'No account owner'} · account owner</p>
              <div className="sideprio">
                <PriorityFlagButton row={account} withLabel
                  priority={{ viewer: book.viewer, pendingId: priority.pendingId, onToggle: priority.toggle }} />
                {priority.note ? <span className="sideprionote" role="status">{priority.note}</span> : null}
              </div>
              {lost && !pastOpportunityIsLoss(past) ? <p className="sidesub">Prior closed-lost deal · {lost.when}</p> : null}
              {past ? <p className="sidesub pastopp">{pastOpportunityChip(past)} · {pastOpportunityDetail(past)}</p> : null}
              <Q4Warnings row={account} />
              {/* The one-line identity strip: size, location, industry, segment. Each falls
                  back to what the OB Engine derived, for the same reason the LinkedIn chip
                  does: on the accounts it has assessed the engine is often the only source we
                  hold. `city` is deliberately not joined in -- it is populated on 2 of 314
                  accounts, so "city, country" was country on every account but two. */}
              <p className="sidesub">
                {employeeCount != null || country || industry || account.segment ? (
                  <Separated
                    parts={[
                      employeeCount != null ? `${formatCount(employeeCount)} employees` : null,
                      country,
                      industry,
                      account.segment,
                    ]}
                  />
                ) : (
                  <span className="absent">No company details in Salesforce.</span>
                )}
              </p>
              {/* The SDR's last send is the strategist's header line; an AE's strip has the deal. */}
              {view.factStrip === 'ae' ? null : lastOut ? (
                <p className="lastout">
                  Last out <b>{lastOut.day}</b>
                  {lastOut.who ? <> to <b>{lastOut.who}</b></> : ' with no contact recorded'}
                  {lastOut.author ? (
                    <>
                      {', by '}
                      {lastOut.byOther
                        ? <b className="otherrep">{lastOut.author}</b>
                        : <>{lastOut.author}</>}
                    </>
                  ) : null}
                </p>
              ) : (
                <p className="lastout absent">Nobody has sent anything to this account.</p>
              )}
              <SideLinks row={account} />
            </div>

            {showHiddenNote ? (
              <div className="hiddennote" role="status">
                <span>Hidden from your board.</span>
                <button type="button" className="btn" disabled={prefPending} onClick={() => {
                  setPreference(account.id, hiddenHere?.previous ?? null);
                }}>Undo</button>
              </div>
            ) : preference === 'hidden' ? (
              <div className="hiddennote" role="status"><span>Hidden from your board.</span></div>
            ) : null}
            <FactStrip facts={stripFacts(view.factStrip, account, book.today)} />
            <AccountPanel row={account} book={book} onContactEnriched={onContactEnriched} workReason={workReason}
              view={view} />
          </CollapseProvider>
        ) : null}
      </aside>
    </>
  );
}
